import { useState } from 'react';
import type { JSX } from 'react';
import Container from '../components/Container';
import Button from '../components/Button';
import Eyebrow from '../components/Eyebrow';
import CodeWindow, { KW, STR, COM } from '../components/CodeWindow';
import { ArrowRight, Check, Shield, FileLock2, Lock, ScrollText, Github } from 'lucide-react';

type Flow = 'oauth' | 'magic' | 'mfa' | 'session' | 'scim' | 'webhooks';

const flows: { id: Flow; label: string; method: string; filename: string }[] = [
  { id: 'oauth', label: 'OAuth', method: 'GET / POST', filename: 'auth/oauth-callback.ts' },
  { id: 'magic', label: 'Magic Link', method: 'POST', filename: 'auth/magic-link.ts' },
  { id: 'mfa', label: 'MFA Challenge', method: 'POST', filename: 'auth/mfa.ts' },
  { id: 'session', label: 'Session Refresh', method: 'POST', filename: 'auth/session.ts' },
  { id: 'scim', label: 'SCIM', method: 'GET / POST', filename: 'scim/users.ts' },
  { id: 'webhooks', label: 'Webhooks', method: 'POST', filename: 'webhooks.ts' },
];

const code: Record<Flow, JSX.Element> = {
  oauth: (
    <pre className="whitespace-pre">
      <KW>import</KW> {'{ Pryma } '}
      <KW>from</KW> <STR>'pryma'</STR>
      {'\n\n'}
      <KW>const</KW> pryma = <KW>new</KW> Pryma({'({ projectId: '}
      <STR>'proj_a5n'</STR>
      {' })'}
      {'\n\n'}
      <COM>// 1. Redirect user to GitHub</COM>
      {'\n'}
      <KW>const</KW> url = <KW>await</KW> pryma.auth.oauth.start({'({'}
      {'\n  provider: '}
      <STR>'github'</STR>,{'\n  redirectTo: '}
      <STR>'/dashboard'</STR>,{'\n})'}
      {'\n\n'}
      <COM>// 2. Exchange code for session</COM>
      {'\n'}
      <KW>const</KW> {'{ user, session } = '}
      <KW>await</KW> pryma.auth.oauth.callback({'({'}
      {'\n  code, state,\n})'}
      {'\n\n'}
      <COM>// Account already linked? Returns the same user.</COM>
      {'\n'}
      <KW>return</KW> user
    </pre>
  ),
  magic: (
    <pre className="whitespace-pre">
      <KW>import</KW> {'{ Pryma } '}
      <KW>from</KW> <STR>'pryma'</STR>
      {'\n\n'}
      <KW>const</KW> pryma = <KW>new</KW> Pryma({'({ projectId: '}
      <STR>'proj_a5n'</STR>
      {' })'}
      {'\n\n'}
      <COM>// 1. Send the link</COM>
      {'\n'}
      <KW>await</KW> pryma.auth.magicLink.send({'({'}
      {'\n  email: '}
      <STR>'kira@stellar.dev'</STR>,{'\n  redirectTo: '}
      <STR>'/dashboard'</STR>,{'\n})'}
      {'\n\n'}
      <COM>// 2. Verify the token from the link in your /verify route</COM>
      {'\n'}
      <KW>const</KW> {'{ user, session } = '}
      <KW>await</KW> pryma.auth.magicLink.verify({'({'}
      {'\n  token: req.query.token,\n})'}
    </pre>
  ),
  mfa: (
    <pre className="whitespace-pre">
      <COM>// After password sign-in, challenge for MFA</COM>
      {'\n'}
      <KW>const</KW> challenge = <KW>await</KW> pryma.auth.mfa.challenge({'({'}
      {'\n  userId: user.id,\n  factor: '}
      <STR>'totp'</STR>,{'\n})'}
      {'\n\n'}
      <COM>// User enters their 6-digit code</COM>
      {'\n'}
      <KW>const</KW> {'{ session } = '}
      <KW>await</KW> pryma.auth.mfa.verify({'({'}
      {'\n  challengeId: challenge.id,\n  code: req.body.code,\n})'}
      {'\n\n'}
      <COM>// Step-up enforced based on risk score & role</COM>
    </pre>
  ),
  session: (
    <pre className="whitespace-pre">
      <COM>// Refresh tokens rotate automatically — but here's the manual flow</COM>
      {'\n'}
      <KW>const</KW> refreshed = <KW>await</KW> pryma.auth.session.refresh({'({'}
      {'\n  refreshToken: cookies.get('}
      <STR>'pryma_rt'</STR>
      {'),'}
      {'\n})'}
      {'\n\n'}
      <COM>// Revoke from any device, anywhere</COM>
      {'\n'}
      <KW>await</KW> pryma.auth.session.revoke({'({ sessionId: refreshed.id })'}
      {'\n\n'}
      <COM>// Or revoke all sessions for a user</COM>
      {'\n'}
      <KW>await</KW> pryma.auth.session.revokeAll({'({ userId: user.id })'}
    </pre>
  ),
  scim: (
    <pre className="whitespace-pre">
      <COM>// SCIM 2.0 endpoints exposed at /scim/v2/* automatically.</COM>
      {'\n'}
      <COM>// Configure the IdP, paste the bearer token, ship.</COM>
      {'\n\n'}
      <KW>const</KW> token = <KW>await</KW> pryma.scim.tokens.create({'({'}
      {'\n  name: '}
      <STR>'okta-prod'</STR>,{'\n  expiresIn: '}
      <STR>'90d'</STR>,{'\n})'}
      {'\n\n'}
      <COM>// JIT or full sync — both supported. Group → role mapping built-in.</COM>
      {'\n'}
      <KW>await</KW> pryma.scim.groups.map({'({ scimGroup: '}
      <STR>'eng-leads'</STR>,{' role: '}
      <STR>'admin'</STR>
      {' })'}
    </pre>
  ),
  webhooks: (
    <pre className="whitespace-pre">
      <COM>// Subscribe to any auth event — HMAC-signed, retried with backoff.</COM>
      {'\n'}
      <KW>await</KW> pryma.webhooks.create({'({'}
      {'\n  url: '}
      <STR>'https://api.stellar.dev/pryma'</STR>,{'\n  events: ['}
      <STR>'auth.sign_in.success'</STR>
      {', '}
      <STR>'auth.session.revoked'</STR>
      {'],'}
      {'\n  secret: process.env.PRYMA_WEBHOOK_SECRET,\n})'}
      {'\n\n'}
      <COM>// In your handler:</COM>
      {'\n'}
      pryma.webhooks.verify({'(req.headers, req.body)'}
    </pre>
  ),
};

const features = [
  [
    '01',
    'Multi-factor authentication',
    'TOTP, push, recovery codes, and step-up challenges — enforced per role or risk score.',
  ],
  [
    '02',
    'Sessions & refresh tokens',
    'Configurable TTLs, automatic rotation, session revocation across all devices.',
  ],
  [
    '03',
    'SCIM provisioning',
    'Just-in-time and full sync. Pre-cert’d for Okta, Workday, and Azure AD.',
  ],
  [
    '04',
    'SSO · SAML & OIDC',
    'Per-tenant IdP, group mapping, JIT user creation, custom claim mapping.',
  ],
  [
    '05',
    'Webhook events',
    'Every auth event, HMAC-signed, retried with backoff, queryable for 30 days.',
  ],
  [
    '06',
    'RBAC & permissions',
    'Hierarchical roles, scoped permissions, every check audit-trailed by default.',
  ],
] as const;

export default function AuthDetail() {
  const [activeFlow, setActiveFlow] = useState<Flow>('oauth');
  const flow = flows.find(f => f.id === activeFlow)!;

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-slate50">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-24 mx-auto h-[420px] max-w-[1100px] opacity-60 blur-3xl"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 50%, rgba(124,58,237,0.14) 0%, rgba(124,58,237,0.05) 45%, transparent 70%)',
          }}
        />
        <Container className="relative py-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-violetLine bg-violetSoft px-3 py-1 text-[12px] font-semibold text-violet">
            <span className="size-1.5 rounded-full bg-violet" />
            Auth · v1.0 · production-ready
          </span>
          <h1 className="mt-6 max-w-[820px] text-[64px] font-bold leading-[1.04] tracking-tighter text-ink sm:text-[80px]">
            Auth, settled.
          </h1>
          <div className="mt-6 h-[3px] w-[96px] rounded-full bg-violet" />
          <p className="mt-6 max-w-[680px] text-[18px] leading-[1.55] text-body">
            Production-grade OAuth, magic links, MFA, sessions, SCIM, audit logs, and webhooks. The
            auth layer your senior engineers wished they'd built.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button variant="primary" size="lg">
              Start with Auth <ArrowRight className="size-4" />
            </Button>
            <Button variant="secondary" size="lg">
              <Github className="size-4" /> View on GitHub
            </Button>
          </div>

          {/* trust ticker */}
          <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border bg-surface px-5 py-3 font-mono text-[12px] text-body shadow-hair">
            <span className="inline-flex items-center gap-1.5 font-semibold text-ink">
              <span className="size-1.5 rounded-full bg-ok" /> 2.4M sign-ins yesterday
            </span>
            <span className="text-borderStrong">·</span>
            <span>SOC 2 Type II</span>
            <span className="text-borderStrong">·</span>
            <span>99.99% uptime</span>
            <span className="text-borderStrong">·</span>
            <span>last incident: 84 days ago</span>
            <span className="ml-auto font-semibold text-violet">status.pryma.dev →</span>
          </div>
        </Container>
      </section>

      {/* API SURFACE */}
      <section>
        <Container className="py-24">
          <Eyebrow color="violet">API Surface</Eyebrow>
          <h2 className="mt-3 text-[40px] font-bold tracking-tighter text-ink">Pick a flow.</h2>
          <p className="mt-3 max-w-[640px] text-[16px] text-body">
            Each tab is a canonical implementation — copy, paste, ship.
          </p>

          {/* tab bar */}
          <div className="mt-8 flex items-center justify-between gap-4 overflow-x-auto rounded-xl border border-border bg-surface px-2">
            <div className="flex items-center">
              {flows.map(f => {
                const active = f.id === activeFlow;
                return (
                  <button
                    key={f.id}
                    onClick={() => setActiveFlow(f.id)}
                    className={`relative px-4 py-3 text-[14px] transition-colors ${
                      active ? 'font-semibold text-violet' : 'text-body hover:text-ink'
                    }`}
                  >
                    {f.label}
                    {active && (
                      <span className="absolute inset-x-3 bottom-0 h-[2px] rounded-full bg-violet" />
                    )}
                  </button>
                );
              })}
            </div>
            <span className="hidden whitespace-nowrap rounded-full border border-violetLine bg-violetSoft px-2.5 py-1 font-mono text-[11px] font-semibold text-violet sm:inline">
              {flow.method}
            </span>
          </div>

          {/* split: code | what you get */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <CodeWindow
                filename={flow.filename}
                edge
                status={{ dot: 'bg-ok', label: 'TS — strict' }}
                meta="every option type-checked"
              >
                {code[activeFlow]}
              </CodeWindow>
            </div>
            <aside className="lg:col-span-5">
              <div className="rounded-2xl border border-border bg-slate50 p-6">
                <Eyebrow color="violet">What you get</Eyebrow>
                <h3 className="mt-2 text-[20px] font-bold text-ink">{flow.label} flow includes:</h3>
                <ul className="mt-5 space-y-4">
                  {[
                    [
                      '8 providers built-in',
                      'GitHub, Google, Microsoft, Okta, Discord, Apple, GitLab + custom OIDC providers',
                    ],
                    [
                      'Account linking, verified',
                      'Same email → same user, magic-link verification before silent merge.',
                    ],
                    ['Refresh tokens, rotated', 'Stored encrypted, rotated automatically.'],
                    ['Audit log per sign-in', 'IP, device, provider, outcome — queryable.'],
                    ['Configurable session TTL', 'Per provider, per project, per role.'],
                    ['SCIM-ready', 'Provisioning for Okta, Workday, Azure AD.'],
                  ].map(([t, d]) => (
                    <li key={t} className="flex gap-3">
                      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border border-violet bg-violetSoft">
                        <Check className="size-3 text-violet" />
                      </span>
                      <div>
                        <p className="text-[14px] font-semibold text-ink">{t}</p>
                        <p className="mt-0.5 text-[12px] text-body">{d}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <a
                  href="#"
                  className="mt-6 inline-flex items-center gap-1 border-t border-border pt-5 text-[13px] font-semibold text-violet hover:text-violetMid"
                >
                  Browse all auth flows <ArrowRight className="size-3.5" />
                </a>
              </div>
            </aside>
          </div>
        </Container>
      </section>

      {/* COMPLIANCE BAND */}
      <section className="border-y border-border bg-slate50">
        <Container className="py-20">
          <Eyebrow color="violet">Built for scale</Eyebrow>
          <h2 className="mt-3 text-[32px] font-bold tracking-tighter text-ink">
            The boring parts, settled.
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <FileLock2 className="size-4 text-violet" />,
                t: 'SOC 2 Type II',
                d: 'Audited annually. Pull our report from the Trust Center.',
              },
              {
                icon: <Shield className="size-4 text-violet" />,
                t: 'GDPR & CCPA',
                d: 'EU residency option, full data export, scheduled erasure.',
              },
              {
                icon: <ScrollText className="size-4 text-violet" />,
                t: 'Audit log everything',
                d: 'Every event, every user, every session — streamed or queried.',
              },
              {
                icon: <Lock className="size-4 text-violet" />,
                t: 'Encrypted everywhere',
                d: 'AES-256 at rest, TLS 1.3 in flight. Per-tenant key rotation.',
              },
            ].map(c => (
              <div
                key={c.t}
                className="rounded-2xl border border-border bg-surface p-5 shadow-hair"
              >
                <div className="grid size-9 place-items-center rounded-lg border border-violetLine bg-violetSoft">
                  {c.icon}
                </div>
                <p className="mt-4 text-[14px] font-bold text-ink">{c.t}</p>
                <p className="mt-1 text-[12px] leading-[1.5] text-body">{c.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FEATURE GRID */}
      <section>
        <Container className="py-24">
          <Eyebrow color="violet">Everything you expect</Eyebrow>
          <h2 className="mt-3 text-[40px] font-bold tracking-tighter text-ink">
            More than just sign-in.
          </h2>
          <p className="mt-3 max-w-[680px] text-[16px] text-body">
            Six built-in primitives engineers usually re-implement badly. Wired in, audited.
          </p>

          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {features.map(([n, t, d]) => (
              <div
                key={n}
                className="group rounded-2xl border border-border bg-surface p-6 shadow-hair transition-shadow hover:shadow-elev"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[12px] font-bold text-violet">{n}</span>
                  <span className="text-[16px] font-bold text-ink">{t}</span>
                </div>
                <p className="mt-3 text-[13px] leading-[1.55] text-body">{d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* INTEGRATIONS */}
      <section className="border-y border-border bg-slate50">
        <Container className="py-12">
          <p className="text-center text-[11px] font-bold uppercase tracking-widest text-muted">
            Works with
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[18px] font-bold tracking-tight text-body">
            {[
              'Next.js',
              'Remix',
              'Express',
              'Hono',
              'Bun',
              'Deno',
              'Rails',
              'Django',
              'FastAPI',
            ].map(f => (
              <span key={f}>{f}</span>
            ))}
            <a href="#" className="text-violet hover:text-violetMid">
              +more →
            </a>
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section>
        <Container className="py-24">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 -bottom-24 mx-auto h-[200px] max-w-[1000px] opacity-50 blur-3xl"
            style={{
              background: 'radial-gradient(closest-side, rgba(124,58,237,0.15), transparent)',
            }}
          />
          <div className="relative mx-auto max-w-[860px] text-center">
            <Eyebrow color="muted">Ready?</Eyebrow>
            <h2 className="mt-5 text-[44px] font-bold leading-[1.05] tracking-tighter text-ink sm:text-[52px]">
              Auth your engineers will thank you for.
            </h2>
            <p className="mx-auto mt-5 max-w-[560px] text-[16px] text-body">
              Free for 50,000 monthly active users. No credit card required to start.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button variant="primary" size="lg">
                Start with Auth <ArrowRight className="size-4" />
              </Button>
              <Button variant="secondary" size="lg">
                Talk to founders
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </main>
  );
}
